import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { isEqual } from 'lodash';
import type { HipSpineUserPreferenceData } from '@/stores/hipSpinePreferences/types';
import {
    DataState,
    makeDataState,
    withStateLoading,
    withStateSaving,
} from '@/util/dataState';
import type { AxiosRequestConfig } from 'axios';
import {
    loadHipSpineUserPreferences,
    saveHipSpinePreferences,
} from '@/api/hipSpinePreferences/services';

const emptyPreferenceData = (): HipSpineUserPreferenceData => ({
    minCupAnteversionBound: null,
    maxCupAnteversionBound: null,
    minCupInclinationBound: null,
    maxCupInclinationBound: null,
});

export const useHipSpinePreferences = defineStore('hip-spine-user-preferences', () => {
    const current = reactive<HipSpineUserPreferenceData>(emptyPreferenceData());
    const lastSaved = reactive<HipSpineUserPreferenceData>(emptyPreferenceData());
    const _stateHolder = reactive(makeDataState());

    const isSaving = computed(() => _stateHolder.state === DataState.Saving);
    const isError = computed(() => _stateHolder.state === DataState.Error);
    const isReady = computed(() => _stateHolder.state !== DataState.Loading);
    const error = computed(() => _stateHolder.error);

    const hasUnsavedChanges = computed(() => !isEqual(lastSaved, current));

    const allFieldsFilled = computed(() =>
        Object.values(current).every((value) => typeof value === 'number'),
    );

    const _clearState = () => {
        Object.assign(current, emptyPreferenceData());
        Object.assign(lastSaved, emptyPreferenceData());
        Object.assign(_stateHolder, makeDataState());
    };

    const load = async (userId: number, config?: AxiosRequestConfig): Promise<void> => {
        _clearState();

        await withStateLoading(
            _stateHolder,
            async () => {
                const preferencesData = await loadHipSpineUserPreferences(userId, config);
                if (preferencesData) {
                    Object.assign(current, preferencesData);
                }
                Object.assign(lastSaved, { ...current });
            },
            config?.signal,
        );
    };

    const save = async (userId: number, config?: AxiosRequestConfig): Promise<void> => {
        if (!allFieldsFilled.value) return;

        await withStateSaving(
            _stateHolder,
            async () => {
                const snapshot = { ...current };
                await saveHipSpinePreferences(userId, snapshot, config);
                Object.assign(lastSaved, snapshot);
            },
            config?.signal,
        );
    };

    return {
        current,
        isSaving,
        isError,
        isReady,
        error,
        hasUnsavedChanges,
        allFieldsFilled,
        $reset: _clearState,
        load,
        save,
    };
});
